import { FormEvent, useEffect, useState, type ChangeEvent } from "react";
import { FileText, FilePlus2, ScrollText, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import type { FarmerAgreement, AgreementAmendment } from "@/types/domain";
import { PageHeader } from "@/components/PageHeader";
import { LoadingState } from "@/components/shared/StatePanels";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const STATUS_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  DRAFT: "secondary",
  PENDING: "secondary",
  EXECUTED: "default",
  ACTIVE: "default",
  EXPIRED: "outline",
  TERMINATED: "destructive",
};

export default function AgreementsPage() {
  const [agreements, setAgreements] = useState<FarmerAgreement[]>();
  const [amendments, setAmendments] = useState<Record<string, AgreementAmendment[]>>({});
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showAmendmentFor, setShowAmendmentFor] = useState<string | null>(null);

  const load = async () => {
    const result = await services.agreements.listAgreements();
    setAgreements(result.items);
    const amdMap: Record<string, AgreementAmendment[]> = {};
    for (const agr of result.items) {
      try {
        const amdResult = await services.agreements.listAmendments(agr.id);
        amdMap[agr.id] = amdResult.items;
      } catch { amdMap[agr.id] = []; }
    }
    setAmendments(amdMap);
  };

  useEffect(() => { load(); }, []);

  if (!agreements) return <LoadingState label="Loading agreements" />;

  const filtered = agreements.filter((a) =>
    !search || a.agreementReference.toLowerCase().includes(search.toLowerCase()) ||
    (a.farmerName || "").toLowerCase().includes(search.toLowerCase()) ||
    a.status.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="agreements-page">
      <PageHeader
        eyebrow="Legal & Records"
        title="Agreements & Records"
        description="Farmer commercial agreements, amendments, consent linkage, and document references. Reuses existing private buckets and consent structures."
        action={
          <Button onClick={() => setShowCreate(true)} data-testid="agreements-create-button">
            <FilePlus2 className="mr-2 size-4" /> New agreement
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          <div className="flex items-center gap-3 border-b px-4 py-3">
            <Input
              placeholder="Search by reference, farmer, or status…"
              value={search}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              className="max-w-xs"
              data-testid="agreements-search-input"
            />
          </div>
          <Table data-testid="agreements-table">
            <TableHeader>
              <TableRow>
                <TableHead>Reference</TableHead>
                <TableHead>Farmer</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Share</TableHead>
                <TableHead>Term</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Amendments</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((agr) => {
                const amdList = amendments[agr.id] || [];
                return (
                  <TableRow key={agr.id} data-testid={`agreement-row-${agr.id}`}>
                    <TableCell className="font-mono text-xs">{agr.agreementReference}</TableCell>
                    <TableCell>{agr.farmerName || "Unknown farmer"}</TableCell>
                    <TableCell className="text-xs">{agr.agreementType}</TableCell>
                    <TableCell>
                      <span className="font-medium">{agr.farmerSharePct}%</span> / <span className="text-muted-foreground">{agr.companySharePct}%</span>
                    </TableCell>
                    <TableCell className="text-xs">
                      {agr.effectiveDate} → {agr.expiryDate}
                      <p className="text-muted-foreground">{agr.signedDate ? `Signed ${agr.signedDate}` : "Unsigned"}</p>
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[agr.status] || "default"} data-testid={`agreement-status-${agr.id}`}>{agr.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {amdList.length > 0 ? (
                        <div className="space-y-1">
                          {amdList.map((amd) => (
                            <div key={amd.id} className="text-xs" data-testid={`amendment-${amd.id}`}>
                              <span className="font-medium">{amd.amendmentReference}</span>
                              <Badge variant={STATUS_VARIANT[amd.status] || "default"} className="ml-2" data-testid={`amendment-status-${amd.id}`}>{amd.status}</Badge>
                              {amd.termYears && <span className="ml-2 text-muted-foreground">{amd.termYears}yr</span>}
                              {amd.signedDate && <span className="ml-2 text-muted-foreground">{amd.signedDate}→{amd.expiryDate}</span>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">None</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowAmendmentFor(agr.id)}
                        data-testid={`agreement-add-amendment-${agr.id}`}
                      >
                        <ScrollText className="mr-1 size-3" /> Amendment
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground" data-testid="agreements-empty-state">
                    No agreements found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <ShieldCheck className="size-5 text-primary" />
            <div className="space-y-1">
              <p className="text-sm font-semibold">Storage & privacy</p>
              <p className="text-xs text-muted-foreground">
                Agreement document references point to the existing private <code>farmer-documents</code> bucket.
                Access is gated by RLS and audited storage access tickets. No private objects are exposed publicly.
                Consent signatures remain in <code>farmer-consent</code>. Event timestamps (captured_at) are preserved
                separately from sync timestamps (synced_at) for offline and online records.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {showCreate && (
        <CreateAgreementDialog
          onClose={() => { setShowCreate(false); }}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {showAmendmentFor && (
        <CreateAmendmentDialog
          agreementId={showAmendmentFor}
          onClose={() => { setShowAmendmentFor(null); }}
          onCreated={() => { setShowAmendmentFor(null); load(); }}
        />
      )}
    </div>
  );
}

function CreateAgreementDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({
    farmerId: "10000000-0000-4000-8000-000000000001",
    agreementType: "COMMERCIAL_SHARE",
    agreementReference: "",
    farmerSharePct: "60",
    companySharePct: "40",
    signedDate: "",
    effectiveDate: "",
    expiryDate: "",
    status: "ACTIVE",
  });
  const [error, setError] = useState("");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.agreementReference || !form.effectiveDate || !form.expiryDate) {
      setError("Reference, effective date, and expiry date are required.");
      return;
    }
    if (Number(form.farmerSharePct) + Number(form.companySharePct) !== 100) {
      setError("Farmer share + company share must equal 100%.");
      return;
    }
    if (form.expiryDate <= form.effectiveDate) {
      setError("Expiry date must be after effective date.");
      return;
    }
    setError("");
    try {
      await services.agreements.createAgreement({
        farmer_id: form.farmerId,
        agreement_type: form.agreementType,
        agreement_reference: form.agreementReference,
        farmer_share_pct: Number(form.farmerSharePct),
        company_share_pct: Number(form.companySharePct),
        signed_date: form.signedDate || undefined,
        effective_date: form.effectiveDate,
        expiry_date: form.expiryDate,
        status: form.status,
      });
      toast("Agreement created", { description: form.agreementReference });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create agreement");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="create-agreement-dialog">
      <Card className="w-full max-w-2xl">
        <CardContent className="p-6">
          <div className="mb-4 flex items-center gap-2">
            <FileText className="size-5 text-primary" />
            <h2 className="text-lg font-semibold">New farmer agreement</h2>
          </div>
          <form onSubmit={submit} className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Farmer ID</Label>
              <Input value={form.farmerId} onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, farmerId: e.target.value })} className="font-mono text-xs" data-testid="create-agreement-farmer-id" />
            </div>
            <div className="space-y-2">
              <Label>Agreement type</Label>
              <Select value={form.agreementType} onValueChange={(v: string) => setForm({ ...form, agreementType: v })}>
                <SelectTrigger data-testid="create-agreement-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="COMMERCIAL_SHARE">Commercial share</SelectItem>
                  <SelectItem value="SOLE_OFFTAKE">Sole off-take</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Agreement reference</Label>
              <Input value={form.agreementReference} onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, agreementReference: e.target.value })} placeholder="AR-AGR-XXXXX" data-testid="create-agreement-reference-input" />
            </div>
            <div className="space-y-2">
              <Label>Farmer share %</Label>
              <Input type="number" value={form.farmerSharePct} onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, farmerSharePct: e.target.value })} data-testid="create-agreement-farmer-share" />
            </div>
            <div className="space-y-2">
              <Label>Company share %</Label>
              <Input type="number" value={form.companySharePct} onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, companySharePct: e.target.value })} data-testid="create-agreement-company-share" />
            </div>
            <div className="space-y-2">
              <Label>Signed date</Label>
              <Input type="date" value={form.signedDate} onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, signedDate: e.target.value })} data-testid="create-agreement-signed-date" />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v: string) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="create-agreement-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="EXECUTED">Executed</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Effective date</Label>
              <Input type="date" value={form.effectiveDate} onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, effectiveDate: e.target.value })} data-testid="create-agreement-effective-date" />
            </div>
            <div className="space-y-2">
              <Label>Expiry date</Label>
              <Input type="date" value={form.expiryDate} onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, expiryDate: e.target.value })} data-testid="create-agreement-expiry-date" />
            </div>
            {error && <p className="text-sm text-destructive md:col-span-2" data-testid="create-agreement-error">{error}</p>}
            <div className="flex justify-end gap-2 md:col-span-2">
              <Button type="button" variant="outline" onClick={onClose} data-testid="create-agreement-cancel">Cancel</Button>
              <Button type="submit" data-testid="create-agreement-submit">Create agreement</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function CreateAmendmentDialog({ agreementId, onClose, onCreated }: { agreementId: string; onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({
    amendmentType: "SOLE_OFFTAKE_EXTENSION",
    amendmentReference: "",
    termYears: "9",
  });
  const [error, setError] = useState("");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.amendmentReference) {
      setError("Amendment reference is required.");
      return;
    }
    setError("");
    try {
      await services.agreements.createAmendment(agreementId, {
        original_agreement_id: agreementId,
        amendment_type: form.amendmentType,
        amendment_reference: form.amendmentReference,
        status: "DRAFT",
        term_years: Number(form.termYears),
      });
      toast("Amendment created (DRAFT)", { description: "Amendment is not legally active until executed." });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create amendment");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="create-amendment-dialog">
      <Card className="w-full max-w-lg">
        <CardContent className="p-6">
          <div className="mb-4 flex items-center gap-2">
            <ScrollText className="size-5 text-primary" />
            <h2 className="text-lg font-semibold">Add amendment</h2>
          </div>
          <div className="mb-4 rounded-lg border border-warning/20 bg-warning/10 p-3 text-xs text-warning" data-testid="amendment-draft-notice">
            Amendment starts in DRAFT status. It is not legally active until signed and executed.
          </div>
          <form onSubmit={submit} className="grid gap-4">
            <div className="space-y-2">
              <Label>Amendment type</Label>
              <Select value={form.amendmentType} onValueChange={(v: string) => setForm({ ...form, amendmentType: v })}>
                <SelectTrigger data-testid="create-amendment-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SOLE_OFFTAKE_EXTENSION">Sole off-take extension</SelectItem>
                  <SelectItem value="SHARE_ADJUSTMENT">Share adjustment</SelectItem>
                  <SelectItem value="TERM_EXTENSION">Term extension</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Amendment reference</Label>
              <Input value={form.amendmentReference} onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, amendmentReference: e.target.value })} placeholder="AR-AMD-XXXX" data-testid="create-amendment-reference-input" />
            </div>
            <div className="space-y-2">
              <Label>Term years</Label>
              <Input type="number" value={form.termYears} onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, termYears: e.target.value })} data-testid="create-amendment-term-years" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="create-amendment-error">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose} data-testid="create-amendment-cancel">Cancel</Button>
              <Button type="submit" data-testid="create-amendment-submit">Create amendment</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
